import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { STANDARD_TIER_MONTHLY_CENTS, formatMonthly } from '@/components/ui/TierBadge'
import { Loader2, Shield, AlertTriangle, Gift, DollarSign, FlaskConical, PauseCircle } from 'lucide-react'

type Tier = 'associate' | 'partner' | 'principal'

interface BillingOverrideState {
  orgId: string
  orgName: string
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  subscriptionTier: string
  isComped: boolean
  billingPaused: boolean
  customMonthlyCents: number | null
  stripe: { status: string; currentPeriodEnd: number; cancelAtPeriodEnd: boolean } | null
  activeCoupon: { id: string; name: string | null; percentOff: number | null } | null
  activePriceId: string | null
  activePriceAmountCents: number | null
  audit: Array<{
    id: string
    action: string
    details: Record<string, any>
    createdAt: string
    actorEmail: string | null
    actorName: string | null
  }>
}

interface Props {
  orgId: string
  /** Called after any mutation (tier swap, comp toggle, custom price) so the
      parent page can refetch its own org snapshot. Without this, actions like
      "View as this org" read stale tier data captured at initial page load. */
  onOrgChanged?: () => void
}

function formatMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

function formatAuditAction(action: string): string {
  return action.replace(/_/g, ' ')
}

export default function BillingOverrideSection({ orgId, onOrgChanged }: Props) {
  const [state, setState] = useState<BillingOverrideState | null>(null)
  const [loading, setLoading] = useState(true)
  const [compSubmitting, setCompSubmitting] = useState(false)
  const [pauseSubmitting, setPauseSubmitting] = useState(false)
  const [priceSubmitting, setPriceSubmitting] = useState(false)
  const [priceTier, setPriceTier] = useState<Tier>('principal')
  const [priceInput, setPriceInput] = useState('')
  const [dbTierSubmitting, setDbTierSubmitting] = useState<Tier | 'trial' | null>(null)
  const { toast } = useToast()

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/platform/organizations/${orgId}/billing/override`)
      const data: BillingOverrideState = res.data.data
      setState(data)
      if (data.customMonthlyCents !== null) {
        setPriceInput((data.customMonthlyCents / 100).toFixed(2))
      }
      if (['associate', 'partner', 'principal'].includes(data.subscriptionTier)) {
        setPriceTier(data.subscriptionTier as Tier)
      }
    } catch (err: any) {
      toast({
        title: 'Failed to load billing overrides',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  /**
   * Flip the org's DB tier with NO Stripe side effects — for testing feature
   * gating. Calls PUT /platform/organizations/:id which only writes
   * subscription_tier to the organizations table; no customer/sub/invoice
   * will be created in Stripe.
   *
   * Also flips subscription_status to 'active' for the paid tiers (and
   * 'trialing' for the trial tier) so the hybrid-trial baseline doesn't
   * mask the tier we're actually trying to test. During a trial the
   * feature gate grants Partner-level access to everyone regardless of
   * tier — which is great for real customers but useless for testing
   * Associate-specific gating. Forcing status=active here means "pretend
   * this org has finished their trial and is paying at the selected tier."
   */
  const setDbTier = async (tier: Tier | 'trial') => {
    setDbTierSubmitting(tier)
    try {
      const subscriptionStatus = tier === 'trial' ? 'trialing' : 'active'
      await api.put(`/api/platform/organizations/${orgId}`, {
        subscriptionTier: tier,
        subscriptionStatus,
      })
      toast({
        title: 'Tier updated (DB only)',
        description: `Org is now ${tier}${tier !== 'trial' ? ' + status=active' : ''}. No Stripe changes made.`,
      })
      await load()
      onOrgChanged?.()
    } catch (err: any) {
      toast({
        title: 'Tier update failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setDbTierSubmitting(null)
    }
  }

  const toggleComp = async (nextComp: boolean) => {
    setCompSubmitting(true)
    try {
      await api.post(`/api/platform/organizations/${orgId}/billing/comp`, {
        comp: nextComp,
        fallbackTier: 'principal',
      })
      toast({
        title: nextComp ? 'Comp enabled' : 'Comp removed',
        description: nextComp
          ? 'Org now pays $0 via permanent 100% off coupon.'
          : 'Coupon detached. Normal billing resumes next invoice.',
      })
      await load()
      onOrgChanged?.()
    } catch (err: any) {
      toast({
        title: 'Comp toggle failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setCompSubmitting(false)
    }
  }

  const togglePause = async (nextPause: boolean) => {
    setPauseSubmitting(true)
    try {
      await api.post(`/api/platform/organizations/${orgId}/billing/pause`, {
        pause: nextPause,
      })
      toast({
        title: nextPause ? 'Billing paused' : 'Billing resumed',
        description: nextPause
          ? 'Stripe will not charge this customer until billing is resumed.'
          : 'Stripe invoice collection resumed — normal billing continues.',
      })
      await load()
      onOrgChanged?.()
    } catch (err: any) {
      toast({
        title: 'Pause toggle failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setPauseSubmitting(false)
    }
  }

  const applyCustomPrice = async () => {
    const parsed = parseFloat(priceInput)
    if (Number.isNaN(parsed) || parsed < 0) {
      toast({ title: 'Invalid price', description: 'Enter a positive dollar amount.', variant: 'destructive' })
      return
    }
    setPriceSubmitting(true)
    try {
      await api.post(`/api/platform/organizations/${orgId}/billing/custom-price`, {
        monthlyCents: Math.round(parsed * 100),
        tier: priceTier,
      })
      toast({
        title: 'Custom price applied',
        description: `Org now billed $${parsed.toFixed(2)}/mo on ${priceTier}.`,
      })
      await load()
      onOrgChanged?.()
    } catch (err: any) {
      toast({
        title: 'Custom price failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setPriceSubmitting(false)
    }
  }

  const clearCustomPrice = async () => {
    setPriceSubmitting(true)
    try {
      await api.post(`/api/platform/organizations/${orgId}/billing/custom-price`, {
        monthlyCents: null,
        tier: priceTier,
      })
      toast({
        title: 'Standard pricing applied',
        description: `Org now on the standard ${priceTier} plan.`,
      })
      setPriceInput('')
      await load()
      onOrgChanged?.()
    } catch (err: any) {
      toast({
        title: 'Failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setPriceSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading billing state…
        </CardContent>
      </Card>
    )
  }

  if (!state) return null

  const hasStripe = Boolean(state.stripeCustomerId && state.stripeSubscriptionId)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-violet-500" />
            <CardTitle className="text-base">Billing override</CardTitle>
          </div>
          <CardDescription>
            Super-admin-only. Mutations are audited. Changes take effect on the next invoice.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Stripe state */}
          <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Stripe customer</span>
              <span className="font-mono text-xs">{state.stripeCustomerId ?? 'not created'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subscription</span>
              <span className="font-mono text-xs">
                {state.stripeSubscriptionId ?? 'none'}
                {state.stripe && (
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    {state.stripe.status}
                  </Badge>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current tier</span>
              <span className="capitalize">{state.subscriptionTier}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Active price</span>
              <span>{formatMoney(state.activePriceAmountCents)}/mo</span>
            </div>
            {state.activeCoupon && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active coupon</span>
                <span className="text-emerald-600 dark:text-emerald-400">
                  {state.activeCoupon.name ?? state.activeCoupon.id}
                  {state.activeCoupon.percentOff && ` — ${state.activeCoupon.percentOff}% off`}
                </span>
              </div>
            )}
          </div>

          {!hasStripe && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <p>
                No Stripe customer or subscription on file yet. The first comp/price action below
                will lazy-create both (trial subscription on Principal by default) before applying.
              </p>
            </div>
          )}

          {/* Testing shortcut — flips DB tier only, never touches Stripe. Kept
              separate from the Stripe-connected controls below so it's obvious
              that this path produces no invoices, customers, or dunning emails. */}
          <div className="rounded-lg border border-dashed border-sky-500/40 bg-sky-500/5 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              <p className="font-medium text-sm">Set tier for testing (DB only, no Stripe)</p>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-sky-500/40 text-sky-600 dark:text-sky-400">
                NO STRIPE CHANGES
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Writes <code className="text-[11px]">subscription_tier</code> directly to the DB so you can
              impersonate and verify feature gating at that tier. No Stripe customer, subscription, or
              invoice is created. Use the controls below instead when you want real billing to kick in.
            </p>
            <div className="flex flex-wrap gap-2">
              {(['associate', 'partner', 'principal', 'trial'] as const).map((t) => {
                const isCurrent = state.subscriptionTier === t
                return (
                  <Button
                    key={t}
                    size="sm"
                    variant={isCurrent ? 'default' : 'outline'}
                    disabled={dbTierSubmitting !== null || isCurrent}
                    onClick={() => setDbTier(t)}
                    className="capitalize"
                  >
                    {dbTierSubmitting === t ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                    {t}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* Comp toggle */}
          <div className="rounded-lg border p-4 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-violet-500" />
                <p className="font-medium text-sm">Comp this org ($0 billing)</p>
                {state.isComped && (
                  <Badge className="text-[10px] bg-violet-500 hover:bg-violet-500">ACTIVE</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Applies a permanent 100% off coupon. Customer keeps their tier label; invoices show
                full price, then a matching discount, then $0.00 due. Reversible anytime.
              </p>
            </div>
            <Switch
              checked={state.isComped}
              disabled={compSubmitting}
              onCheckedChange={toggleComp}
            />
          </div>

          {/* Pause billing toggle */}
          <div className="rounded-lg border p-4 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <PauseCircle className="h-4 w-4 text-amber-500" />
                <p className="font-medium text-sm">Pause billing</p>
                {state.billingPaused && (
                  <Badge className="text-[10px] bg-amber-500 hover:bg-amber-500">PAUSED</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Pauses Stripe invoice collection. The customer is not charged while paused and is
                not back-charged on resume. The subscription stays active. Reversible anytime.
              </p>
            </div>
            <Switch
              checked={state.billingPaused}
              disabled={pauseSubmitting || !hasStripe}
              onCheckedChange={togglePause}
            />
          </div>

          {/* Tier + pricing override */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-brand-royal" />
              <p className="font-medium text-sm">Plan & pricing</p>
              {state.customMonthlyCents !== null && (
                <Badge className="text-[10px]" variant="secondary">
                  CUSTOM · {formatMoney(state.customMonthlyCents)}/mo
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Pick a tier and either use its standard price or override with a negotiated amount.
              Custom prices create a one-off Stripe Price and swap it onto the subscription with
              no immediate proration (takes effect next invoice).
            </p>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[120px]">
                <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
                  Tier
                </label>
                <Select value={priceTier} onValueChange={(v) => setPriceTier(v as Tier)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="associate">Associate · {formatMonthly(STANDARD_TIER_MONTHLY_CENTS.associate)}/mo</SelectItem>
                    <SelectItem value="partner">Partner · {formatMonthly(STANDARD_TIER_MONTHLY_CENTS.partner)}/mo</SelectItem>
                    <SelectItem value="principal">Principal · {formatMonthly(STANDARD_TIER_MONTHLY_CENTS.principal)}/mo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-[11px] text-muted-foreground uppercase tracking-wider block mb-1">
                  Custom amount ($)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder={`leave blank for ${formatMonthly(STANDARD_TIER_MONTHLY_CENTS[priceTier])}`}
                  disabled={priceSubmitting}
                  className="h-9"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={clearCustomPrice}
                disabled={priceSubmitting}
              >
                Use standard {priceTier} price ({formatMonthly(STANDARD_TIER_MONTHLY_CENTS[priceTier])}/mo)
              </Button>
              <Button
                size="sm"
                onClick={applyCustomPrice}
                disabled={priceSubmitting || !priceInput}
              >
                {priceSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply custom amount'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit trail */}
      {state.audit.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Audit trail</CardTitle>
            <CardDescription>Most recent 20 billing-override actions on this org.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {state.audit.map((entry) => (
                <div key={entry.id} className="py-2 flex items-start justify-between gap-4 text-xs">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium capitalize">{formatAuditAction(entry.action)}</p>
                    <p className="text-muted-foreground truncate">
                      {entry.actorName ?? entry.actorEmail ?? 'unknown actor'}
                      {entry.details?.tier && ` · tier: ${entry.details.tier}`}
                      {entry.details?.monthlyCents !== undefined &&
                        ` · amount: ${formatMoney(entry.details.monthlyCents)}`}
                    </p>
                  </div>
                  <span className="text-muted-foreground shrink-0">
                    {new Date(entry.createdAt).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
